export interface TabStripItem<V extends string> {
  value: V;
  label: string;
  count?: number;
  /** Доступное имя вкладки. Без него берётся `label`. Именно сюда вызывающая
   *  сторона (`kind-tabs.tsx`, `ifi-source-tabs.tsx`) кладёт число обратно
   *  словом: голые цифры внутри вкладки помечены `aria-hidden` (см. ниже), но
   *  сам счётчик — не украшение, а причина существования этих строк вкладок:
   *  пользователь скринридера должен услышать, какие чипы пустые, ДО того
   *  как переключится, иначе он жмёт вкладку вслепую и слышит пустой список. */
  ariaLabel?: string;
}

/**
 * Презентационная строка вкладок: разметка, стили и доступность, без данных.
 * Счётчики, i18n и работа с URL — на вызывающей стороне (см. `kind-tabs.tsx`,
 * `ifi-source-tabs.tsx`), поэтому одну и ту же полосу можно переиспользовать
 * для разных осей фильтра, не копируя разметку.
 */
export function TabStrip<V extends string>({
  ariaLabel,
  items,
  active,
  onSelect,
}: {
  ariaLabel: string;
  items: TabStripItem<V>[];
  active: V;
  onSelect: (value: V) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="mt-3 flex items-stretch gap-1 overflow-x-auto border-b border-edge"
    >
      {items.map((item) => {
        const isActive = active === item.value;
        return (
          <button
            key={item.value || 'all'}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={item.ariaLabel ?? item.label}
            onClick={() => onSelect(item.value)}
            className={`-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-semibold transition-colors ${
              isActive
                ? 'border-brand text-brand'
                : 'border-transparent text-ink-soft hover:text-ink'
            }`}
          >
            {item.label}
            {item.count != null && (
              // aria-hidden: голое число — не осмысленное доступное имя вкладки
              // (иначе оно склеилось бы в «Все2688»). Само число объявляется
              // через aria-label, который собирает вызывающая сторона.
              <span
                aria-hidden="true"
                className={`text-[11px] font-medium tabular-nums ${
                  isActive ? 'text-brand' : 'text-ink-muted'
                }`}
              >
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
